// CriticalPathNotasApi.js
import axios from "axios";
import { API_URL } from "../apiConfig";

// =====================================
// 🔐 Token Helper
// =====================================
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// =====================================
// 🔔 NOTAS – BADGE SIDEBAR
// =====================================

// Obtener número de notas no leídas por usuario
export const getNotasNuevas = async (userId) => {
  if (!userId) throw new Error("userId es obligatorio");

  const response = await axios.get(
    `${API_URL}/api/criticalpath/notas/nuevas/${userId}`,
    { headers: authHeader() },
  );

  return response.data; // { total, porProducto }
};

// =====================================
// 📋 RESUMEN BULK (NotasResumen hub)
// =====================================

export const getNotasResumenClientes = async ({ userId }) => {
  if (!userId) throw new Error("userId es obligatorio");

  const response = await axios.get(
    `${API_URL}/api/criticalpath/notas/resumen/clientes`,
    {
      params: { userId },
      headers: authHeader(),
    },
  );

  return response.data; // { clientes }
};

export const getNotasResumen = async ({
  userId,
  entityType = "ALL",
  includeClosed = "all",
  includeNotes = false,
  cliente,
}) => {
  if (!userId) throw new Error("userId es obligatorio");

  const includeClosedParam =
    includeClosed === "all"
      ? "all"
      : includeClosed
        ? "1"
        : "0";

  const params = {
    userId,
    entityType,
    includeClosed: includeClosedParam,
  };

  const trimmedCliente = String(cliente || "").trim();
  if (trimmedCliente) {
    params.cliente = trimmedCliente;
  }

  if (includeNotes) {
    params.includeNotes = "1";
  } else {
    params.includeNotes = "0";
  }

  const response = await axios.get(
    `${API_URL}/api/criticalpath/notas/resumen`,
    {
      params,
      headers: authHeader(),
    },
  );

  return response.data; // { blocks }
};

/** Intenta resumen rápido; si el backend aún no lo soporta, reintenta sin includeNotes. */
export const getNotasResumenWithFallback = async (options) => {
  try {
    return await getNotasResumen({ ...options, includeNotes: false });
  } catch (fastError) {
    const status = fastError?.response?.status;
    if (status !== 500 && status !== 400) {
      throw fastError;
    }
    console.warn(
      "Resumen rápido no disponible, reintentando modo legacy…",
      fastError?.response?.data,
    );
    const { includeNotes: _omit, ...legacyOptions } = options;
    const legacyParams = { ...legacyOptions };
    return getNotasResumen({ ...legacyParams, includeNotes: true });
  }
};

// =====================================
// 📝 NOTAS POR PRODUCTO (CON isRead)
// =====================================

// Obtener notas de un producto para un usuario
export const getNotasByEntityWithUser = async ({
  productId = null,
  idMuestra = null,
  idPresupuestoA3Pedido = null,
  userId,
}) => {
  const idPed =
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
      ? Number(idPresupuestoA3Pedido)
      : null;
  if (!userId || (!productId && !idMuestra && !idPed)) {
    throw new Error("Entidad y userId obligatorios");
  }

  const response = await axios.get(
    `${API_URL}/api/criticalpath/notas/${productId || "null"}`,
    {
      params: {
        userId,
        idMuestra,
        ...(idPed != null ? { idPresupuestoA3Pedido: idPed } : {}),
      },
      headers: authHeader(),
    },
  );

  return response.data;
};

// =====================================
// 📊 TOTAL NOTAS POR ENTIDAD (SIN FILTRO LEÍDAS)
// =====================================

export const getNotasCountAll = async () => {
  const response = await axios.get(
    `${API_URL}/api/criticalpath/notas/count-all`,
    { headers: authHeader() },
  );

  return response.data; // [{ ProductId, ID_MUESTRA, total }]
};

// =====================================
// 👁️ MARCAR NOTAS COMO LEÍDAS
// =====================================

// Marcar notas como leídas (por producto o global)
/* export const marcarNotasLeidasPorProducto = async (productId, userId) => {
  if (!userId) throw new Error("userId es obligatorio");

  return axios.post(
    `${API_URL}/api/criticalpath/notas/marcar-leidas`,
    {
      productId: productId || null, // null = todas
      userId,
    },
    { headers: authHeader() },
  );
}; */

export const marcarNotasLeidasPorEntity = async ({
  productId = null,
  idMuestra = null,
  idPresupuestoA3Pedido = null,
  userId,
}) => {
  const idPed =
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
      ? Number(idPresupuestoA3Pedido)
      : null;
  if (!userId || (!productId && !idMuestra && !idPed)) {
    throw new Error("Entidad y userId obligatorios");
  }

  return axios.post(
    `${API_URL}/api/criticalpath/notas/marcar-leidas`,
    {
      productId,
      idMuestra,
      idPresupuestoA3Pedido: idPed,
      userId,
    },
    { headers: authHeader() },
  );
};

// SUBIR NOTAS CON ARCHIVOS
/* export const addNotaToProduct = async (data) => {
  const res = await axios.post(`${API_URL}/api/criticalpath/notas`, data, {
    headers: authHeader(),
  });
  return res.data; // 👈 debe incluir notaId
};
 */

export const addNotaToEntity = async (data) => {
  let { productId, idMuestra, idPresupuestoA3Pedido } = data;

  const normalizedProductId =
    productId !== undefined &&
    productId !== null &&
    productId !== "null" &&
    productId !== ""
      ? Number(productId)
      : null;

  const normalizedIdMuestra =
    idMuestra !== undefined &&
    idMuestra !== null &&
    idMuestra !== "null" &&
    idMuestra !== ""
      ? Number(idMuestra)
      : null;

  const normalizedIdPed =
    idPresupuestoA3Pedido !== undefined &&
    idPresupuestoA3Pedido !== null &&
    idPresupuestoA3Pedido !== "null" &&
    idPresupuestoA3Pedido !== ""
      ? Number(idPresupuestoA3Pedido)
      : null;

  if (
    (!normalizedProductId || normalizedProductId <= 0) &&
    (!normalizedIdMuestra || normalizedIdMuestra <= 0) &&
    (!normalizedIdPed || normalizedIdPed <= 0)
  ) {
    throw new Error("ProductId, ID_MUESTRA o IdPresupuestoA3Pedido obligatorio");
  }

  const res = await axios.post(
    `${API_URL}/api/criticalpath/notas`,
    {
      ...data,
      productId: normalizedProductId,
      idMuestra: normalizedIdMuestra,
      idPresupuestoA3Pedido: normalizedIdPed,
    },
    { headers: authHeader() },
  );

  return res.data;
};

// =====================================
// 📎 ADJUNTOS DE NOTAS
// =====================================

export const getAdjuntosByNota = async (notaId) => {
  if (!notaId) throw new Error("notaId es obligatorio");

  const res = await axios.get(
    `${API_URL}/api/criticalpath/notas-files/${notaId}/adjuntos`,
    { headers: authHeader() },
  );

  return res.data;
};

export const uploadAdjuntosNota = async ({
  notaId,
  productId = null,
  idMuestra = null,
  idPresupuestoA3Pedido = null,
  files,
}) => {
  const idPed =
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
      ? Number(idPresupuestoA3Pedido)
      : null;
  if (!notaId || (!productId && !idMuestra && !idPed)) {
    throw new Error("notaId y entidad obligatorios");
  }

  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));

  const res = await axios.post(
    `${API_URL}/api/criticalpath/notas-files/upload`,
    formData,
    {
      params: {
        notaId,
        productId,
        idMuestra,
        ...(idPed != null ? { idPresupuestoA3Pedido: idPed } : {}),
      },
      headers: {
        ...authHeader(),
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return res.data;
};

export const updateNotaEntity = async (notaId, data) => {
  return axios.put(`${API_URL}/api/criticalpath/notas/${notaId}`, data, {
    headers: authHeader(),
  });
};

export const deleteNotaEntity = async (notaId) => {
  return axios.delete(`${API_URL}/api/criticalpath/notas/${notaId}`, {
    headers: authHeader(),
  });
};
